import React from 'react'
import { observer } from 'mobx-react-lite'

import List from '@mui/material/List'
import ListSubheader from '@mui/material/ListSubheader'
import Switch from '@mui/material/Switch'

import { Icon, Text } from '@mxenabled/mxui'

import AccountActionsRow from './AccountActionsRow'

import {
  ANALYTICS_EVENTS,
  Dialog,
  Drawer,
  useAccountStore,
  useEvent,
  useGlobalCopyStore,
  useTransactionStore,
} from '../../../../common'
import { type Account } from '../../../../core'
import { useAccountUiStore } from '../../../hooks/useUiStore'
import { useGetMergeableAccounts } from '../../../hooks/useGetMergeableAccounts'
import AccountListItem from '../../AccountListItem'
import { buildDynamicCopy } from '../../../../core/utils/Localization'

type UpdatedValue = Pick<Account, 'is_closed'> | Pick<Account, 'is_hidden'>

const AccountActions = () => {
  const [shouldShowCloseAccountModal, setShouldShowCloseAccountModal] = React.useState(false)
  const [shouldShowAccountMergeDrawer, setShouldShowAccountMergeDrawer] = React.useState(false)
  const [shouldShowMergeConfirmModal, setShouldShowMergeConfirmModal] = React.useState(false)
  const [accountToMerge, setAccountToMerge] = React.useState({} as Account)

  const { selectedAccount: account } = useAccountUiStore()
  const { mergeAccounts, updateAccount } = useAccountStore()
  const { accounts: accountsCopy } = useGlobalCopyStore()
  const mergeableAccounts = useGetMergeableAccounts()
  const { onEvent } = useEvent()
  const { modifyTransactionByAccountGuid } = useTransactionStore()

  const handleUpdateAccount = async (updatedValue: UpdatedValue) => {
    await updateAccount({
      ...account,
      ...updatedValue,
    })
    if ('is_closed' in updatedValue && updatedValue.is_closed !== account.is_closed)
      modifyTransactionByAccountGuid(account.guid, { accountIsClosed: updatedValue.is_closed })
  }

  const handleMergeAccounts = () => {
    mergeAccounts([account.guid, accountToMerge.guid]).finally(() =>
      setShouldShowAccountMergeDrawer(false),
    )
  }

  return (
    <>
      <ListSubheader sx={{ backgroundColor: 'transparent', mb: 4, mt: 12, mx: 8 }}>
        <Text bold={true} component="h2" variant="caption">
          {accountsCopy.actions}
        </Text>
      </ListSubheader>

      <AccountActionsRow
        ariaPressed={account.is_hidden}
        onClick={() => {
          handleUpdateAccount({ is_hidden: !account.is_hidden })
          onEvent(ANALYTICS_EVENTS.ACCOUNT_DETAILS_CLICK_HIDE, { account_guid: account.guid })
        }}
        primaryText={account.is_hidden ? accountsCopy.unhide_account : accountsCopy.hide_Account}
        secondaryText={
          account.is_hidden ? accountsCopy.include_this_account : accountsCopy.exclude_this_account
        }
      >
        <Switch aria-hidden={true} checked={account.is_hidden} tabIndex={-1} />
      </AccountActionsRow>

      <AccountActionsRow
        onClick={() => {
          setShouldShowCloseAccountModal(true)
          onEvent(ANALYTICS_EVENTS.ACCOUNT_DETAILS_CLICK_MARK_CLOSED, {
            account_guid: account.guid,
          })
        }}
        primaryText={account.is_closed ? accountsCopy.mark_as_open : accountsCopy.mark_as_closed}
        secondaryText={
          account.is_closed
            ? accountsCopy.this_will_set_the_closed_account
            : accountsCopy.this_will_set_the_account_balance_to_0
        }
      >
        <Icon name="chevron_right" />
        {/* Non-interactive wrapper: only stops click propagation to the parent
            ListItemButton so clicks inside the Dialog don't re-trigger it. */}
        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions, jsx-a11y/click-events-have-key-events */}
        <div onClick={(e) => e.stopPropagation()}>
          <Dialog
            copy={{
              close_aria: accountsCopy.close_modal,
              title: account.is_closed
                ? accountsCopy.confirm_mark_as_open
                : accountsCopy.confirm_mark_closed,
            }}
            isOpen={shouldShowCloseAccountModal}
            onClose={() => {
              setShouldShowCloseAccountModal(false)
            }}
            onPrimaryAction={() => handleUpdateAccount({ is_closed: !account.is_closed })}
          >
            <Text truncate={false}>
              {
                buildDynamicCopy(
                  account.is_closed
                    ? accountsCopy.this_will_resume_updating_the_balance
                    : accountsCopy.this_will_set_the_balance_of_your_account,
                  account.name,
                ) as string
              }
            </Text>
          </Dialog>
        </div>
      </AccountActionsRow>

      {mergeableAccounts.length > 0 && (
        <AccountActionsRow
          isLast={true}
          onClick={() => {
            setShouldShowAccountMergeDrawer(true)
            onEvent(ANALYTICS_EVENTS.ACCOUNT_DETAILS_CLICK_MARK_DUPLICATE, {
              account_guid: account.guid,
            })
          }}
          primaryText={accountsCopy.mark_as_duplicate}
          secondaryText={accountsCopy.if_a_connected_account_is_showing_up_twice}
        >
          <Icon name="chevron_right" />
        </AccountActionsRow>
      )}

      <Drawer
        ariaLabelClose={accountsCopy.close_drawer}
        isOpen={shouldShowAccountMergeDrawer}
        onClose={() => setShouldShowAccountMergeDrawer(false)}
        title={accountsCopy.mark_as_duplicate}
      >
        <List>
          {mergeableAccounts.map((account: Account) => {
            const isLast = mergeableAccounts.indexOf(account) === mergeableAccounts.length - 1
            return (
              <AccountListItem
                account={account}
                key={account.guid}
                listItemProps={{
                  sx: {
                    position: 'relative',
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      bottom: 0,
                      right: 0,
                      left: isLast ? 0 : 24,
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  },
                }}
                onRowClick={() => {
                  setShouldShowMergeConfirmModal(true)
                  setAccountToMerge(account)
                }}
              />
            )
          })}
        </List>
        {shouldShowMergeConfirmModal && (
          <Dialog
            copy={{
              close_aria: accountsCopy.close_modal,
              title: accountsCopy.confirm_merge,
            }}
            isOpen={shouldShowMergeConfirmModal}
            onClose={() => setShouldShowMergeConfirmModal(false)}
            onPrimaryAction={handleMergeAccounts}
          >
            <Text truncate={false}>
              {
                buildDynamicCopy(
                  accountsCopy.this_means_accounts_will_be_merged,
                  account.name,
                  accountToMerge.name,
                ) as string
              }
            </Text>
            <Text color="error" sx={{ mt: 24 }}>
              {accountsCopy.this_action_cannot_be_undone}
            </Text>
          </Dialog>
        )}
      </Drawer>
    </>
  )
}

export default observer(AccountActions)
